'use client'

import { useCallback, useEffect, useRef, useState } from 'react'
import { AnimatePresence, LazyMotion, domAnimation, m, useReducedMotion } from 'framer-motion'
import { MOTION_DURATION, MOTION_EASE } from '@/lib/motion-theme'

export const IOS_DOWNLOAD_EVENT = 'tls:ios-download-completed'

/**
 * Where focus goes when the overlay closes if the control that opened it is
 * gone. On /sounds/[id] the Download button unmounts the moment the download
 * completes and the receipt takes its slot, so the receipt's heading is the
 * honest place to land (it is the element the receipt focuses itself).
 */
const FALLBACK_RESTORE_ID = 'post-download-receipt-heading'

export function IOSDownloadOverlay() {
  const [open, setOpen] = useState(false)
  const prefersReducedMotion = useReducedMotion()
  const dialogRef = useRef<HTMLDivElement | null>(null)
  const closeButtonRef = useRef<HTMLButtonElement | null>(null)
  const previouslyFocusedRef = useRef<HTMLElement | null>(null)

  useEffect(() => {
    const handler = () => setOpen(true)
    window.addEventListener(IOS_DOWNLOAD_EVENT, handler)
    return () => window.removeEventListener(IOS_DOWNLOAD_EVENT, handler)
  }, [])

  const close = useCallback(() => setOpen(false), [])

  // Dialog focus contract, the same one components/ui/report-modal.tsx keeps:
  // move focus into the dialog on open, trap Tab inside it, close on Escape,
  // and hand focus back to the trigger (or the receipt heading) on close.
  useEffect(() => {
    if (!open) return

    previouslyFocusedRef.current =
      typeof document !== 'undefined' ? (document.activeElement as HTMLElement | null) : null
    closeButtonRef.current?.focus()

    const handleKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        close()
        return
      }
      if (event.key === 'Tab' && dialogRef.current) {
        const focusable = Array.from(
          dialogRef.current.querySelectorAll<HTMLElement>(
            'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
          )
        ).filter((el) => el.tabIndex !== -1)
        if (focusable.length === 0) return
        const first = focusable[0]
        const last = focusable[focusable.length - 1]
        if (event.shiftKey) {
          if (document.activeElement === first) {
            event.preventDefault()
            last.focus()
          }
        } else if (document.activeElement === last) {
          event.preventDefault()
          first.focus()
        }
      }
    }
    window.addEventListener('keydown', handleKeyDown)

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
      // The Download button that opened this is disabled while the download
      // is in flight, and Chrome blurs a disabled control, so by the time the
      // effect ran the "previously focused" element was <body> (measured).
      // <body>, a detached element and a disabled one are all unusable
      // restore targets; the receipt heading is the honest fallback.
      const prev = previouslyFocusedRef.current
      const usable =
        !!prev &&
        prev !== document.body &&
        prev.isConnected &&
        !(prev as HTMLButtonElement).disabled &&
        typeof prev.focus === 'function'
      const target = usable ? prev : document.getElementById(FALLBACK_RESTORE_ID)
      if (target) {
        // Defer restore so the dialog is fully unmounted before focus returns.
        queueMicrotask(() => target.focus())
      }
    }
  }, [open, close])

  return (
    <LazyMotion features={domAnimation}>
      <AnimatePresence>
        {open && (
          <m.div
            key="ios-download-overlay"
            initial={{ opacity: 0 }}
            animate={{
              opacity: 1,
              transition: {
                duration: prefersReducedMotion ? MOTION_DURATION.fast : MOTION_DURATION.standard,
                ease: MOTION_EASE.emphasis,
              },
            }}
            exit={{
              opacity: 0,
              transition: { duration: MOTION_DURATION.fast, ease: MOTION_EASE.emphasis },
            }}
            className="fixed inset-0 z-[200] flex items-end justify-center sm:items-center"
            style={{
              background: 'var(--tlsr-overlay-scrim)',
              paddingTop: 'env(safe-area-inset-top)',
              paddingBottom: 'env(safe-area-inset-bottom)',
            }}
            onClick={close}
          >
            <m.div
              ref={dialogRef}
              initial={
                prefersReducedMotion
                  ? { opacity: 0 }
                  : { opacity: 0, transform: 'translateY(24px) scale(0.97)' }
              }
              animate={{
                opacity: 1,
                ...(prefersReducedMotion
                  ? {}
                  : { transform: 'translateY(0px) scale(1)' }),
                transition: {
                  duration: prefersReducedMotion ? MOTION_DURATION.fast : MOTION_DURATION.emphasis,
                  ease: MOTION_EASE.emphasis,
                },
              }}
              exit={{
                opacity: 0,
                ...(prefersReducedMotion
                  ? {}
                  : { transform: 'translateY(24px) scale(0.97)' }),
                transition: { duration: MOTION_DURATION.fast, ease: MOTION_EASE.emphasis },
              }}
              role="dialog"
              aria-modal="true"
              aria-labelledby="ios-download-overlay-title"
              className="m-4 max-w-md w-full rounded-[var(--ls-radius-0)] p-6 shadow-none"
              style={{
                background: 'var(--tls-surface-elevated)',
                border: '1px solid var(--tls-border-strong)',
                color: 'var(--tls-fg-strong)',
                fontFamily: 'var(--font-body)',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-start gap-3">
                <div
                  className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full"
                  style={{ background: 'var(--tls-brand)', color: 'var(--tls-brand-fg)' }}
                  aria-hidden="true"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                </div>
                <div className="flex-1">
                  <h2
                    id="ios-download-overlay-title"
                    className="text-lg font-semibold"
                    style={{ fontFamily: 'var(--font-display)' }}
                  >
                    File downloaded
                  </h2>
                  <p className="mt-2 text-sm leading-relaxed" style={{ color: 'var(--tls-fg-muted)' }}>
                    Open the Safari download manager (
                    <span className="inline-flex items-center align-middle" aria-label="downloads icon">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'inline-block', verticalAlign: 'middle' }}>
                        <path d="M12 3v12" />
                        <path d="m7 10 5 5 5-5" />
                        <path d="M5 21h14" />
                      </svg>
                    </span>
                    {' '}arrow icon, top right of Safari) to find <code className="break-words font-mono text-xs px-1 py-0.5 rounded" style={{ background: 'var(--tls-surface)' }}>LockChime.wav</code>.
                  </p>
                </div>
              </div>
              <button
                ref={closeButtonRef}
                type="button"
                onClick={close}
                className="tlsr-focus mt-5 w-full rounded-[var(--ls-radius-0)] px-4 py-3 text-sm font-medium transition-opacity hover:opacity-90"
                style={{
                  background: 'var(--tls-brand)',
                  color: 'var(--tls-brand-fg)',
                }}
              >
                Got it
              </button>
            </m.div>
          </m.div>
        )}
      </AnimatePresence>
    </LazyMotion>
  )
}
